'use client';

import { Shield, ExternalLink, Zap, CheckCircle, Settings } from 'lucide-react';
import { BRAND } from "@/lib/brand";
import Link from 'next/link';

export default function FigmaIntegrationPage() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-50 border border-gray-200 rounded-2xl mb-6">
          <svg className="w-12 h-12" viewBox="0 0 38 57" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z" fill="#1ABCFE"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z" fill="#0ACF83"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M19 19V38H9.5C4.25329 38 0 33.7467 0 28.5C0 23.2533 4.25329 19 9.5 19H19Z" fill="#A259FF"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M0 9.5C0 4.25329 4.25329 0 9.5 0H19V19H9.5C4.25329 19 0 14.7467 0 9.5Z" fill="#F24E1E"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M19 0H28.5C33.7467 0 38 4.25329 38 9.5C38 14.7467 33.7467 19 28.5 19H19V0Z" fill="#FF7262"/>
          </svg>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Figma Integration</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Share your Figma designs directly to {BRAND.name}. Post updates, screenshots, and design progress to your team's feed.
        </p>
      </div>

      {/* Features */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Zap className="h-5 w-5 text-gray-700" />
          What you can do
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Share designs instantly</h3>
              <p className="text-sm text-gray-600">Post screenshots and links to your Figma files directly from the Figma editor.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Add context</h3>
              <p className="text-sm text-gray-600">Include descriptions, tag teams and projects to keep everyone in the loop.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Stay in your workflow</h3>
              <p className="text-sm text-gray-600">No need to switch apps or take manual screenshots—share from where you work.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Secure connection</h3>
              <p className="text-sm text-gray-600">API keys are encrypted and can be revoked anytime from your settings.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-700" />
          How to set up
        </h2>
        <ol className="space-y-4">
          <li className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-900 rounded-full flex items-center justify-center font-semibold text-sm">
              1
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Install the Figma plugin</h3>
              <p className="text-sm text-gray-600 mb-3">
                Search for "{BRAND.name}" in the Figma Community or install it directly from the link below.
              </p>
              <a
                href="https://www.figma.com/community/plugin/1555629474571334073/design-commons"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 38 57" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z"/>
                  <path d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z"/>
                  <path d="M19 19V38H9.5C4.25329 38 0 33.7467 0 28.5C0 23.2533 4.25329 19 9.5 19H19Z"/>
                  <path d="M0 9.5C0 4.25329 4.25329 0 9.5 0H19V19H9.5C4.25329 19 0 14.7467 0 9.5Z"/>
                  <path d="M19 0H28.5C33.7467 0 38 4.25329 38 9.5C38 14.7467 33.7467 19 28.5 19H19V0Z"/>
                </svg>
                Install Figma Plugin
              </a>
            </div>
          </li>
          <li className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-900 rounded-full flex items-center justify-center font-semibold text-sm">
              2
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Connect your account</h3>
              <p className="text-sm text-gray-600 mb-3">
                Click the button below to authorize the Figma plugin. You'll receive an API key to paste into the plugin.
              </p>
              <Link
                href="/integrations/figma/auth"
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Authorize Figma Plugin
                <ExternalLink className="h-4 w-4" />
              </Link>
            </div>
          </li>
          <li className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-900 rounded-full flex items-center justify-center font-semibold text-sm">
              3
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Start sharing</h3>
              <p className="text-sm text-gray-600">
                Open the plugin in any Figma file, paste your API key, and start posting updates to your team!
              </p>
            </div>
          </li>
        </ol>
      </div>

      {/* Security */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Shield className="h-5 w-5 text-gray-700" />
          Security & privacy
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          The Figma plugin uses a secure API key to connect to your {BRAND.name} account:
        </p>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">•</span>
            <span><strong>API keys are encrypted</strong> — Your key is securely stored and never exposed</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">•</span>
            <span><strong>Revoke anytime</strong> — Disconnect the plugin instantly from your settings</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">•</span>
            <span><strong>No Figma access</strong> — We don't access your Figma files, only what you share</span>
          </li>
        </ul>
        <p className="text-sm text-gray-500 mt-4">
          You can revoke your API key anytime from your account settings.
        </p>
      </div>
    </div>
  );
}
